import { ExhibitionCollectionProjectFragment } from '@/gql/generated/graphql'
import { useProjectStatus } from '@/hooks/use-project-status'
import { ProjectAddToCalendarButton, TrackedAddToCalButtonNames } from '@components/add-to-calendar'
import { AllPurposeLink } from '@components/all-purpose-link'
import { useSoloViewer } from '@components/solo-viewer/provider'
import { ValidStatusType } from '@utils/project'
import { projectShowRoute, projectPurchaseRoute } from '@utils/routes'
import { DetailText, HeadlineText, CalendarPlusIcon } from 'design-configuration/components'
import { motion } from 'motion/react'
import { exhibitionTransition } from './header'
import { Button } from '@components/button'
import Accordion from '@components/accordion'
import { MoreDetailsBlock } from './works'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { useFormattedDate } from '@/hooks/use-formatted-date'

export const WorksDigitalReleaseCard = ({
  project,
  title,
  description,
  moreDetailsBlocks
}: {
  project: ExhibitionCollectionProjectFragment
  title: string
  description: React.ReactElement
  moreDetailsBlocks: { title: string; description: React.ReactElement }[]
}): React.ReactElement => {
  const startTime = new Date(project.auction_start_time ?? project.start_datetime ?? '')
  const endTime = project.auction_end_time ? new Date(project.auction_end_time ?? '') : undefined

  // Note: weird replaces are to get "Dec 03, 2024, 11:00 AM" to "Dec 03 2024 at 11:00 AM"
  const startTimeString = useFormattedDate(startTime, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric'
  })
    ?.replace(', ', ' ')
    ?.replace(', ', ' at ')
  const endTimeString = useFormattedDate(endTime ?? startTime, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric'
  })
    ?.replace(', ', ' ')
    ?.replace(', ', ' at ')

  const { status } = useProjectStatus(project)

  const { setSoloViewerData } = useSoloViewer()

  return (
    <motion.div
      className="border-outline-variant flex h-fit w-full flex-grow flex-col justify-between border"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={exhibitionTransition}
    >
      <div>
        <div className="bg-surface-container p-md lg:p-2xl relative aspect-[3/2] w-full">
          <button
            className="relative flex h-full w-full items-center justify-center"
            onClick={() =>
              setSoloViewerData({
                token: project?.featured_token?.[0] ?? project.oob_tokens[0] ?? undefined,
                tokenList: [...project.tokens, ...project.oob_tokens]
              })
            }
          >
            <div className="relative h-full w-full" style={{ filter: 'drop-shadow(3px 3px 6px rgba(0, 0, 0, 0.20))' }}>
              <ImageWithCustomLoader
                src={project?.featured_token?.[0]?.media_url ?? ''}
                alt="Digital release"
                style={{
                  objectFit: 'contain'
                }}
                sizes="50vw"
                fill
              />
            </div>
          </button>
        </div>
        <div className="p-md">
          <div className="flex items-center justify-between">
            <div>
              <DetailText className="text-on-surface-variant font-medium uppercase">
                {`${startTimeString}${endTime ? ` - ${endTimeString}` : ''}`}
              </DetailText>
              <HeadlineText className="mt-2xs font-light">{title}</HeadlineText>
            </div>
            {status === ValidStatusType.upcoming ? (
              <ProjectAddToCalendarButton
                projects={[project]}
                colorTheme={'inverted-secondary'}
                className="border-outline-variant p-xs px-xs hover:border-outline border"
                buttonName={TrackedAddToCalButtonNames.exhibitionWorksCard}
              >
                <CalendarPlusIcon className="w-md h-md fill-on-surface" />
              </ProjectAddToCalendarButton>
            ) : null}
          </div>
          {description}
          <Accordion title="More details" titleClassName="text-on-surface-variant underline fill-on-surface-variant">
            <div className="gap-y-xs flex flex-col">
              {moreDetailsBlocks.map((block, index) => (
                <MoreDetailsBlock key={index} title={block.title} description={block.description} />
              ))}
            </div>
          </Accordion>
        </div>
      </div>
      <div className="px-md pb-md mt-sm w-full">
        {status === ValidStatusType.complete ? (
          <AllPurposeLink href={projectShowRoute(project.slug)} className="w-full">
            <Button colorTheme={'secondary'} branded className="w-full bg-transparent">
              View the digital collection
            </Button>
          </AllPurposeLink>
        ) : (
          <div className="gap-xs flex w-full flex-col sm:flex-row md:flex-col xl:flex-row">
            <Button
              colorTheme={'secondary'}
              branded
              className="w-full bg-transparent sm:w-1/2 md:w-full xl:w-1/2"
              onClick={() =>
                setSoloViewerData({
                  token: project.tokens[0] ?? project.oob_tokens[0] ?? undefined,
                  tokenList: [...project.tokens, ...project.oob_tokens]
                })
              }
            >
              Explore algorithm
            </Button>
            {status === ValidStatusType.upcoming ? (
              <Button colorTheme={'primary'} branded disabled className="w-full sm:w-1/2 md:w-full xl:w-1/2">
                Starting soon
              </Button>
            ) : (
              <AllPurposeLink
                href={projectPurchaseRoute(project.slug)}
                className="h-full w-full sm:w-1/2 md:w-full xl:w-1/2"
              >
                <Button colorTheme={'primary'} branded className="w-full">
                  Join auction
                </Button>
              </AllPurposeLink>
            )}
          </div>
        )}
      </div>
    </motion.div>
  )
}
